import { createAnalytics } from '@/shared/features/analytics/config/analytics.build';
import { getUserInfo } from '@/shared/features/analytics/utils/commonAttributes';

export interface LoginAnalyticsData {
  userCode?: string;
  email?: string;
  name?: string;
  phone?: string;
}

export interface CompleteRegistrationAnalyticsData {
  registrationMethod: 'phone_otp' | 'email_otp' | 'email_password';
  email?: string;
  phone?: string;
  userCode?: string;
  firstName?: string;
  postcode?: string;
  state?: string;
  city?: string;
}

function formatIndianPhone(phone?: string): string | undefined {
  if (!phone) return undefined;
  return phone.startsWith('+') ? phone : `+91${phone}`;
}

function resolveCompleteRegistrationLocationFields(
  data: CompleteRegistrationAnalyticsData,
): Pick<CompleteRegistrationAnalyticsData, 'firstName' | 'postcode' | 'state' | 'city'> {
  const userInfo = getUserInfo();
  const firstName = data.firstName?.trim();
  const postcode = data.postcode?.trim();
  const state = data.state?.trim();
  const city = data.city?.trim();

  const pincodeFromContext =
    userInfo?.guessedPincode === true || userInfo?.pincode == null || userInfo.pincode === ''
      ? undefined
      : String(userInfo.pincode);

  return {
    firstName: firstName || undefined,
    postcode: postcode || pincodeFromContext,
    state: state || (typeof userInfo?.state === 'string' ? userInfo.state : undefined),
    city:
      city ||
      (typeof userInfo?.cityName === 'string' ? userInfo.cityName : undefined) ||
      (typeof userInfo?.city === 'string' ? userInfo.city : undefined),
  };
}

/**
 * useFireLoginAnalytics
 *
 * Resolves AnalyticsService through the analytics composition root so login
 * tracking does not import the application layer directly.
 */
export function useFireLoginAnalytics() {
  const fireLoginEvent = (data: LoginAnalyticsData) => {
    createAnalytics()
      .then((analytics) => {
        analytics.loginUser({
          userCode: data.userCode ?? '',
          email: data.email,
          firstName: data.name,
          phone: formatIndianPhone(data.phone),
        });
      })
      .catch(() => {
        // silently ignore
      });
  };

  const fireCompleteRegistration = (data: CompleteRegistrationAnalyticsData) => {
    const locationFields = resolveCompleteRegistrationLocationFields(data);
    createAnalytics()
      .then((analytics) => {
        analytics.trackEvent(
          'CompleteRegistration',
          {
            registration_method: data.registrationMethod,
            registration_context: 'other',
            customer_status: 'new',
            email: data.email,
            phone: data.phone,
            userCode: data.userCode,
            firstName: locationFields.firstName,
            postcode: locationFields.postcode,
            state: locationFields.state,
            city: locationFields.city,
          },
          { eventProviders: ['facebook', 'openai'] },
        );
      })
      .catch(() => {
        // silently ignore
      });
  };

  return { fireLoginEvent, fireCompleteRegistration };
}
